import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Sparkles, Crown } from 'lucide-react';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import DeleteAccountCard from '../components/DeleteAccountCard';
import MyDevicesCard from '../components/MyDevicesCard';
import PodcastFeedCard from '../components/PodcastFeedCard';
import { useAuth } from '../context/AuthContext';
import { getTierBadge, effectivePlan } from '../utils/tierBadge';

const API = process.env.REACT_APP_BACKEND_URL;

/** Public identity - display name + avatar. Email stays private. */
const ProfileCard = ({ user, refresh }) => {
  const [name, setName] = useState(user.display_name || '');
  const [avatar, setAvatar] = useState(user.avatar_url || '');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const fileRef = React.useRef(null);

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
      setErr('Image must be JPEG, PNG, WebP or GIF.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setErr('Image must be under 4MB.');
      return;
    }
    setErr(''); setMsg(''); setUploading(true);
    try {
      const presignRes = await fetch(`${API}/api/me/avatar/presign`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content_type: file.type, filename: file.name }),
      });
      const presign = await presignRes.json();
      if (!presignRes.ok) throw new Error(presign.detail || 'Upload prep failed.');
      const putRes = await fetch(presign.upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Upload failed (${putRes.status})`);
      setAvatar(presign.public_url);
      setMsg('Image uploaded - click Save profile to apply.');
    } catch (er) {
      setErr(er.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fetch(`${API}/api/me/profile`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_name: name.trim(), avatar_url: avatar.trim() }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || 'Save failed.');
      setMsg('Profile saved. This is what shows on your comments.');
      await refresh();
    } catch (e2) {
      setErr(e2.message);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} data-testid="profile-card" className="border border-zinc-800 rounded-lg bg-zinc-950 p-5 mb-6 space-y-4">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Public Profile</p>
        <p className="text-white text-lg font-black">How you show up on comments</p>
        <p className="text-zinc-400 text-xs mt-1">Your email stays private. Only your display name + avatar appear next to your comments. Alias is fine.</p>
      </div>
      <div className="flex items-center gap-4">
        {avatar ? (
          <img src={avatar} alt="" className="h-16 w-16 rounded-full object-cover border border-zinc-700" data-testid="profile-avatar-preview" />
        ) : (
          <div className="h-16 w-16 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center text-2xl font-black text-zinc-600">
            {(name || 'A').trim().slice(0, 1).toUpperCase()}
          </div>
        )}
        <div className="flex-1">
          <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Display name</label>
          <input
            type="text" maxLength={40} value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. RidgebackRider"
            data-testid="profile-display-name"
            className="w-full bg-black border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-white text-sm outline-none"
          />
        </div>
      </div>
      <div>
        <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Avatar image</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="profile-avatar-upload"
            className="bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-xs font-black uppercase tracking-widest px-4 py-2.5 rounded"
          >
            {uploading ? 'Uploading…' : 'Upload image'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={onFilePicked}
            className="hidden"
            data-testid="profile-avatar-file"
          />
          <input
            type="url" maxLength={600} value={avatar}
            onChange={(e) => setAvatar(e.target.value)}
            placeholder="…or paste an image URL"
            data-testid="profile-avatar-url"
            className="flex-1 bg-black border border-zinc-700 focus:border-red-500 rounded px-3 py-2 text-white text-sm outline-none"
          />
        </div>
        <p className="text-[10px] text-zinc-600 mt-1">Max 4MB - JPEG, PNG, WebP or GIF. Doesn&apos;t have to be your face.</p>
      </div>
      {msg && <p className="text-emerald-400 text-xs" data-testid="profile-saved">{msg}</p>}
      {err && <p className="text-red-400 text-xs" data-testid="profile-error">{err}</p>}
      <button
        type="submit"
        disabled={busy || uploading}
        data-testid="profile-save"
        className="bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-black text-xs uppercase tracking-[0.3em] px-4 py-2.5 rounded"
      >
        {busy ? 'Saving…' : 'Save profile'}
      </button>
    </form>
  );
};

const PLAN_LABEL = {
  monthly: 'Outlaw Access - Monthly',
  annual: 'Outlaw Access - Annual',
  founding: 'Founding Member (lifetime)',
  free: 'Not subscribed',
};

/** Upsell card shown to unsubscribed accounts. Prominent but not shouty
 *  - a three-tier at-a-glance card that lets them jump straight to Stripe
 *  Checkout without another click. Meter status woven in so readers can
 *  see exactly how much they've used before they upgrade. */
const UpsellCard = ({ meter }) => {
  const nearlyOut = meter && !meter.unlimited && meter.used >= Math.max(meter.limit - 1, 0);
  return (
    <div
      data-testid="account-upsell-card"
      className="relative overflow-hidden mb-6 rounded-lg border-2 border-red-600/70 bg-gradient-to-br from-red-950/60 via-black to-zinc-950 p-6 shadow-[0_0_28px_rgba(220,38,38,0.28)]"
    >
      <div aria-hidden className="pointer-events-none absolute -top-16 -right-16 h-48 w-48 rounded-full bg-red-600/20 blur-3xl" />

      <div className="relative flex items-start gap-3 mb-3">
        <Sparkles className="h-5 w-5 text-red-400 shrink-0 mt-0.5" />
        <div>
          <p className="text-red-400 text-[10px] font-black uppercase tracking-[0.35em] mb-1">Unlock the whole site</p>
          <h2 className="text-2xl sm:text-3xl font-black leading-tight text-white" style={{ fontFamily: 'Georgia, serif' }}>
            You&apos;re not subscribed yet.
          </h2>
        </div>
      </div>

      {meter && !meter.unlimited && (
        <p className="relative text-sm text-zinc-300 mb-4">
          {nearlyOut ? (
            <>
              Reports are for subscribers only.{' '}
              <span className="text-red-300 font-black">Please choose your unlimited subscription plan.</span>
            </>
          ) : (
            <>
              Reports are for subscribers only. Please choose your unlimited subscription plan below.
            </>
          )}
        </p>
      )}

      <ul className="relative text-sm text-zinc-300 space-y-1.5 mb-6 leading-relaxed">
        <li className="flex gap-2"><span className="text-red-400">✓</span> Unlimited stories & 18+ videos</li>
        <li className="flex gap-2"><span className="text-red-400">✓</span> The Rumour File + Gangland Map</li>
        <li className="flex gap-2"><span className="text-red-400">✓</span> Comment on every story</li>
        <li className="flex gap-2"><span className="text-red-400">✓</span> Listen to any article, narrated</li>
      </ul>

      <div className="relative flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <Link
          to="/subscribe"
          data-testid="account-subscribe-cta"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white font-black text-sm uppercase tracking-[0.25em] px-6 py-4 rounded transition-colors shadow-lg shadow-red-950/50"
        >
          <Sparkles className="h-4 w-4" />
          See our plans
        </Link>
        <Link
          to="/subscribe#founding"
          data-testid="account-subscribe-founding"
          className="sm:w-auto inline-flex items-center justify-center gap-2 bg-black hover:bg-zinc-900 border border-yellow-500/60 text-yellow-300 hover:text-yellow-200 font-black text-[11px] uppercase tracking-[0.25em] px-4 py-4 rounded transition-colors whitespace-nowrap"
        >
          <Crown className="h-4 w-4" />
          Founding - $249 one-off
        </Link>
      </div>

      <p className="relative mt-4 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
        From $13/mo AUD · cancel any time · 80 Founding places
      </p>
    </div>
  );
};

const Account = () => {
  const { user, meter, logout, refresh } = useAuth();
  const [params] = useSearchParams();
  const welcome = params.get('welcome') === '1';
  const [busy, setBusy] = useState(false);

  if (!user) {
    return (
      <div className="min-h-screen bg-black text-white">
        <TopNav />
        <main className="max-w-md mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-black mb-4">Log in to see your account</h1>
          <Link to="/login" className="inline-block bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-[0.3em] px-5 py-3 rounded">
            Log in
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/billing/portal`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not open billing portal.');
      window.location.href = data.url;
    } catch (e) {
      alert(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="account-page">
      <TopNav />
      <main className="max-w-2xl mx-auto px-4 py-12">
        {welcome && (
          <div className="mb-6 border border-emerald-700/70 bg-emerald-950/40 rounded px-4 py-3 text-sm">
            Welcome. Your access is live.
          </div>
        )}
        <p className="text-red-500 text-[11px] font-black uppercase tracking-[0.35em] mb-2">Your Account</p>
        <h1 className="text-3xl font-black mb-6" style={{ fontFamily: 'Georgia, serif' }}>{user.email}</h1>

        <div className="border border-zinc-800 rounded-lg bg-zinc-950 p-5 mb-6" data-testid="account-tier-card">
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Plan</p>
          {(() => {
            const eff = effectivePlan(user);
            const badge = getTierBadge(eff);
            // VIP overrides display - say "VIP" even if base plan is free
            const label = eff === 'vip' ? 'VIP' : (PLAN_LABEL[user.plan] || user.plan);
            return (
              <>
                <p
                  className={'text-lg font-black ' + badge.nameClass}
                  style={badge.nameStyle || undefined}
                  data-testid="account-plan"
                >
                  {badge.emoji && (
                    <span aria-hidden className={'mr-1 ' + badge.emojiClass}>{badge.emoji}</span>
                  )}
                  {label}
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  Status: <span className="uppercase tracking-widest">{eff === 'vip' ? 'ACTIVE' : user.plan_status}</span>
                </p>
                <div className="mt-3 flex items-center gap-3 flex-wrap" data-testid="account-tier-badge-row">
                  <span
                    className={
                      'inline-flex items-center gap-2 px-3 py-1.5 rounded font-black uppercase tracking-[0.2em] text-[11px] ' +
                      badge.pillClass
                    }
                    style={(eff === 'founding' || eff === 'vip') ? { boxShadow: '0 0 12px rgba(250,204,21,0.35)' } : undefined}
                  >
                    {badge.emoji && (
                      <span aria-hidden className={badge.emojiClass}>{badge.emoji}</span>
                    )}
                    {badge.label}
                  </span>
                  {user.display_name && (
                    <span className="text-xs text-zinc-500">
                      Comments will show as{' '}
                      <span
                        className={'font-black ' + badge.nameClass}
                        style={badge.nameStyle || undefined}
                      >
                        {badge.emoji && (
                          <span aria-hidden className={'mr-1 ' + badge.emojiClass}>{badge.emoji}</span>
                        )}
                        {user.display_name}
                      </span>
                    </span>
                  )}
                </div>
              </>
            );
          })()}
          {user.plan === 'founding' && user.founder_display_name && (
            <div className="mt-4 border-t border-zinc-800 pt-4">
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Founder name</p>
              <p className="text-white font-bold">
                {user.founder_display_name}{' '}
                <span
                  className={`ml-2 text-[10px] px-2 py-0.5 rounded font-black uppercase tracking-widest ${
                    user.founder_name_status === 'approved'
                      ? 'bg-emerald-950/60 text-emerald-400 border border-emerald-700/60'
                      : user.founder_name_status === 'rejected'
                      ? 'bg-red-950/60 text-red-400 border border-red-700/60'
                      : 'bg-yellow-950/60 text-yellow-300 border border-yellow-700/60'
                  }`}
                >
                  {user.founder_name_status}
                </span>
              </p>
            </div>
          )}
        </div>

        {/* Profile card - display name + avatar. What everyone else sees on
            your comments. Your email stays private. */}
        <ProfileCard user={user} refresh={refresh} />

        {/* My Devices - self-service device list + revoke. Enforces the
            3-devices-in-14-days sharing policy so travellers stay in
            control and don't get false-banned. */}
        <MyDevicesCard />

        {/* Private podcast feed - renders only for subscribers with access. */}
        <PodcastFeedCard />

        {(user.plan === 'monthly' || user.plan === 'annual') && (
          <button
            type="button"
            onClick={openPortal}
            disabled={busy}
            data-testid="account-manage-billing"
            className="w-full sm:w-auto bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-black text-xs uppercase tracking-[0.3em] px-5 py-3 rounded"
          >
            {busy ? 'Loading…' : 'Manage billing on Stripe'}
          </button>
        )}

        {user.plan === 'free' && <UpsellCard meter={meter} />}

        <div className="mt-10 pt-6 border-t border-zinc-800 flex items-center justify-between text-xs">
          <button
            type="button"
            onClick={async () => { await logout(); await refresh(); }}
            data-testid="account-logout"
            className="text-zinc-400 hover:text-white uppercase tracking-widest"
          >
            Log out
          </button>
          <Link to="/founders" className="text-red-400 hover:text-red-300 uppercase tracking-widest">
            View the Founders →
          </Link>
        </div>

        <DeleteAccountCard onDeleted={async () => { await refresh(); }} />
      </main>
      <SiteFooter />
    </div>
  );
};

export default Account;
